import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { beginLogin } from '@/lib/auth/oauth';
import { FullScreenLoader } from '@/components/feedback/FullScreenLoader';
import { useMaintenance } from '@/features/maintenance/hooks/useMaintenance';

/** Upper bound on waiting for the maintenance status before redirecting anyway. */
const MAINTENANCE_WAIT_MS = 3_000;

/** Router state `ProtectedRoute` attaches when it bounces a deep link here. */
interface FromState {
  from?: { pathname?: string; search?: string; hash?: string };
}

/**
 * The page to return to after login, if the router state names one that is
 * safe: an in-app path (single leading `/`), so history state can never send
 * the user to another origin via `//host` or an absolute URL.
 */
function safeReturnTo(state: unknown): string | undefined {
  const from = (state as FromState | null)?.from;
  if (typeof from?.pathname !== 'string') return undefined;
  if (!from.pathname.startsWith('/') || from.pathname.startsWith('//')) return undefined;
  return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
}

/**
 * Login is hosted on the auth server. This page just kicks off the
 * Authorization Code + PKCE redirect; the user authenticates (email/password or
 * social) on the auth-website's page and returns to /auth/callback, which then
 * continues to the page the user originally asked for (`state.from`).
 *
 * The redirect waits for the maintenance status to settle: the gate fails open
 * while loading, so redirecting immediately would carry signed-out users off
 * to the auth server before they could see a whole-app maintenance screen.
 * When `all` is active the gate unmounts this page, so no redirect happens.
 */
export function LoginPage() {
  const { state } = useLocation();
  const { isSettled } = useMaintenance();
  const [waitExpired, setWaitExpired] = useState(false);
  // beginLogin() runs at most once per mount: a status refetch (poll / focus /
  // reconnect) after a failed first request flips `isSettled` off and on again,
  // and a second call would overwrite the PKCE verifier + state while the first
  // redirect is still in flight.
  const started = useRef(false);

  // A hanging status request must not block login forever.
  useEffect(() => {
    const timer = window.setTimeout(() => setWaitExpired(true), MAINTENANCE_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const ready = isSettled || waitExpired;

  useEffect(() => {
    if (!ready || started.current) return;
    started.current = true;
    void beginLogin(safeReturnTo(state));
  }, [ready, state]);

  return <FullScreenLoader />;
}
